import type { FeatureNavNode } from "../contracts";

/**
 * Merges the navigation nodes contributed by every registered feature into a
 * single tree, joining nodes that share an `id` at the same depth.
 *
 * Several CORE features (organization, roles, catalog, subscriptions, and the
 * newer CORE stubs) each contribute a rail node with `id: "core"` so they
 * render as one rail icon with a shared drawer, mirroring how
 * `mergePermissionCatalogs` joins their permission catalogs by segment
 * (REBUILD-PLAN §15.2). Concatenating instead would render the same rail icon
 * once per contributing feature, with duplicate React keys.
 *
 * First writer wins for `titleKey` / `icon` / `path` / `placement` /
 * `disabled` / `component`: features are merged in the registry's stable id
 * order, so the result is deterministic rather than dependent on registration
 * timing. `activePaths` are unioned; `order` takes the lowest defined value so
 * an explicit ordering from any contributor is honoured.
 */
export function mergeFeatureNavNodes(
  nodes: readonly FeatureNavNode[],
): readonly FeatureNavNode[] {
  const byId = new Map<string, FeatureNavNode[]>();

  for (const node of nodes) {
    const existing = byId.get(node.id);

    if (existing === undefined) {
      byId.set(node.id, [node]);
    } else {
      existing.push(node);
    }
  }

  return [...byId.values()].map((group) => {
    const [first] = group as [FeatureNavNode, ...FeatureNavNode[]];

    if (group.length === 1) {
      return first;
    }

    const children = mergeFeatureNavNodes(
      group.flatMap((node) => node.children ?? []),
    );
    const activePaths = [
      ...new Set(group.flatMap((node) => node.activePaths ?? [])),
    ];
    const orders = group
      .map((node) => node.order)
      .filter((order): order is number => order !== undefined);

    return {
      ...first,
      ...(children.length > 0 ? { children } : {}),
      ...(activePaths.length > 0 ? { activePaths } : {}),
      ...(orders.length > 0 ? { order: Math.min(...orders) } : {}),
    };
  });
}
